import React, { useState } from "react";
import { Button, Input, Message, MultiSelect, Switch, Upload } from "../../../BP2Components";
import { SketchPicker } from "react-color";
import IntlMessages from "../../../../util/IntlMessages";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { setWidgetInSheetParams } from "../../../../appRedux/actions/Boards";
import "./section.less";
import { showName, getBase64StringFromDataURL } from "../../../../util/Scripts";
import { useWidgetSettings } from "../../../../util/customHooks";
import { sendFileToCloudStorage, deleteFile } from "../../../../services/restAPI/RestApi";
interface SectionWidgetSettingsProps {
   value: any;
   data: any;
}

const SectionSettings: React.FC<SectionWidgetSettingsProps> = ({ value, data }) => {
   const { allLanguages } = useSelector(({ settings }: any) => settings);
   const { settings, setSettings, handleChange, syncTranslations } = useWidgetSettings(value, {
      sync: [{ keyField: "titleKey", category: "section.name", valuePrefix: "name" }],
      translations: [{ translationKey: value.titleKey }],
   });
   const [displayColorPicker, setDisplayColorPicker] = useState(false);
   const intl = useIntl();
   const dispatch = useDispatch();

   const handleColorClose = () => {
      setDisplayColorPicker(false);
   };
   const handleColorClick = () => {
      setDisplayColorPicker(!displayColorPicker);
   };

   const handleChangeColor = (color) => {
      setSettings({ ...value, fontColor: color.hex });
   };

   const handleDefaultColor = () => {
      setSettings({ ...settings, fontColor: null });
   };

   const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
      e.preventDefault();

      Message.success(intl.formatMessage({ id: "global.saved" }));
      dispatch(setWidgetInSheetParams(settings));
      syncTranslations(settings);
   };
   const handleUploadFile = (info) => {
      if (!info.type.startsWith("image/")) {
         Message.error(intl.formatMessage({ id: "forms.wrongFileType" }));
         return;
      }

      const cleanedType = info.type.replace(/^image\//, "");

      const reader = new FileReader();

      reader.onload = function (loadEvent) {
         const result = loadEvent?.target?.result;

         const base64 = getBase64StringFromDataURL(result);
         if (base64) {
            Message.loading(intl.formatMessage({ id: "global.loading" }));
            try {
               sendFileToCloudStorage(info.name, cleanedType, base64).then((res) => {
                  setSettings({
                     ...settings,
                     imageUrl: res?.data?.cloudStorage?.url,
                  });
               });
               Message.success(`${intl.formatMessage({ id: "forms.uploadSucces" })} ${info.name}`);
            } catch (error) {
               console.log(error);
               Message.error(intl.formatMessage({ id: "forms.uploadFailed" }));
            } finally {
               Message.destroy();
            }
         }
      };
      reader.readAsDataURL(info);
   };

   const handleDeletePhoto = () => {
      Message.loading(intl.formatMessage({ id: "global.loading" }));
      if (settings.imageFileId && !settings.imageUrl) {
         deleteFile(settings.imageFileId)
            .then(() => {
               setSettings({
                  ...settings,
                  imageUrl: null,
               });
               Message.success(intl.formatMessage({ id: "global.done" }));
            })
            .catch(() => {
               Message.error(intl.formatMessage({ id: "global.error" }));
            })
            .finally(() => {
               Message.destroy();
            });
      } else {
         setSettings({
            ...settings,
            imageUrl: null,
         });
         Message.destroy();
      }
   };

   return (
      <form onSubmit={handleSubmit}>
         <div className="widget-settings">
            <div className="widget-settings__inner">
               <div className="widget-settings__inner__label">
                  <IntlMessages id="settings.photo" />:
                  <div className="action-buttons">
                     <Upload onUpload={handleUploadFile}>
                        <Button buttonType={settings.imageUrl ? "disabled" : "default"}>
                           <IntlMessages id="settings.upload" />
                        </Button>
                     </Upload>

                     <div className="photo-button">
                        <Button
                           onClick={handleDeletePhoto}
                           buttonType={
                              settings.imageUrl || settings.imageFileId ? "default" : "disabled"
                           }
                        >
                           <IntlMessages id="delete" />
                        </Button>
                     </div>
                     {settings.imageUrl ? (
                        <div className="photo-button">
                           <img src={settings.imageUrl} alt="" />
                        </div>
                     ) : null}
                  </div>
               </div>
               <p className="widget-settings__inner__label">
                  <IntlMessages id="settings.sectionTitle" />:
                  <Input
                     defaultValue={settings?.titleKey}
                     onChange={(e) => handleChange(e.target.value, "titleKey")}
                     className="section-settings-title"
                  />
               </p>
               {allLanguages.map((item) => (
                  <label key={item.language} className="widget-settings__inner__label">
                     <IntlMessages id={`widgetTitle${item?.language}`} />
                     <Input
                        type="translation"
                        className="translationName"
                        name={`name${item?.language}`}
                        defaultValue={
                           settings[`name${item?.language}`]
                              ? settings[`name${item?.language}`]
                              : showName(`${item?.language}`, value?.titleKey, data)
                        }
                        onChange={(e) => handleChange(e.target.value, e.target.name)}
                        style={{ width: "300px" }}
                     />
                  </label>
               ))}
               <div className="widget-settings__inner__label">
                  <IntlMessages id="settings.linePosition" />:
                  <MultiSelect
                     noOptionsMessage="no options"
                     name="linePosition"
                     size="medium"
                     style={{ width: 200 }}
                     hideArrow={true}
                     closeMenuOnSelect={false}
                     isMulti={false}
                     placeholder={`${(<IntlMessages id="planner.chooseSlot" />)}`}
                     defaultValue={{
                        value: settings?.linePosition || "under",
                        label: settings?.linePosition ? (
                           <IntlMessages id={`settings.${settings?.linePosition}TheTitle`} />
                        ) : (
                           <IntlMessages id={`settings.underTheTitle`} />
                        ),
                     }}
                     options={[
                        { value: "above", label: <IntlMessages id="settings.aboveTheTitle" /> },
                        { value: "under", label: <IntlMessages id="settings.underTheTitle" /> },
                     ]}
                     onChange={(e) => handleChange(e?.value, "linePosition")}
                  />
               </div>
               <div className="widget-settings__inner__label" key="fontColor">
                  <IntlMessages id="settings.fontColour" />:
                  <div>
                     <Button
                        onClick={handleColorClick}
                        className="cp-color"
                        style={{
                           backgroundColor: settings.fontColor,
                        }}
                     >
                        <IntlMessages id="settings.clickToChangeColor" />
                     </Button>
                     <Button onClick={handleDefaultColor}>
                        <IntlMessages id="settings.setDefaultColor" />
                     </Button>
                  </div>
                  {displayColorPicker ? (
                     <div className="cp-popover">
                        <div className="cp-cover" onClick={handleColorClose} />
                        <SketchPicker
                           color={settings.fontColor || "#fff"}
                           onChange={handleChangeColor}
                        />
                     </div>
                  ) : null}
               </div>
               <div className="widget-settings__inner__label">
                  <IntlMessages id="settings.titleVisibility" />:
                  <Switch
                     size="small"
                     checked={settings?.titleVisibility}
                     onChange={(e) => handleChange(e, "titleVisibility")}
                  />
               </div>
               <div className="widget-settings__inner__label">
                  <IntlMessages id="settings.lineVisibility" />:
                  <Switch
                     size="small"
                     checked={settings?.lineVisibility}
                     onChange={(e) => handleChange(e, "lineVisibility")}
                  />
               </div>
            </div>
            <Button buttonType="primary" htmlType="submit" className="section-settings-btn">
               <IntlMessages id="global.preview" />
            </Button>
         </div>
      </form>
   );
};

export default SectionSettings;
